import type { Card } from "@/context/AppContext";

// Pure eligibility helpers for the single-card / playset listing recommendations
// (engines 8 + 9 in /tools/recommendations). A card "escapes the bulk pile"
// once its market price clears the configurable bulk threshold ($0.24 default).

function isActiveUnlisted(c: Card): boolean {
  return c.listingStatus === "Unlisted" && c.quantity > 0;
}

/** Unlisted cards whose market price exceeds the bulk threshold, richest first. */
export function singlesCandidatesFor(cards: Card[], threshold: number): Card[] {
  return cards
    .filter((c) => isActiveUnlisted(c) && c.marketPrice > threshold)
    .sort((a, b) => b.marketPrice - a.marketPrice);
}

export interface PlaysetCandidate {
  /** Stable key — `name|setName|variant|condition`. */
  key: string;
  name: string;
  setName: string;
  variant: string;
  condition: string;
  /** All unlisted stacks that share this identity. */
  cards: Card[];
  /** Summed quantity across the matched stacks. */
  totalQty: number;
  /** Representative per-copy market price (max across the matched stacks). */
  marketPrice: number;
}

function playsetKey(c: Card): string {
  return `${c.name}|${c.setName}|${c.variant}|${c.condition}`;
}

/** Identities with ≥ minQty unlisted copies, ranked by playset value. */
export function playsetCandidatesFor(
  cards: Card[],
  minQty: number,
): PlaysetCandidate[] {
  const groups = new Map<string, Card[]>();
  for (const c of cards) {
    if (!isActiveUnlisted(c)) continue;
    const key = playsetKey(c);
    const arr = groups.get(key) ?? [];
    arr.push(c);
    groups.set(key, arr);
  }

  const result: PlaysetCandidate[] = [];
  for (const [key, group] of groups) {
    const totalQty = group.reduce((s, c) => s + c.quantity, 0);
    if (totalQty < minQty) continue;
    const first = group[0];
    const marketPrice = Math.max(...group.map((c) => c.marketPrice));
    result.push({
      key,
      name: first.name,
      setName: first.setName,
      variant: first.variant,
      condition: first.condition,
      cards: group,
      totalQty,
      marketPrice,
    });
  }

  return result.sort((a, b) => b.marketPrice * minQty - a.marketPrice * minQty);
}

/** Unique stack ids whose combined quantity covers one playset (≥ minQty). */
export function playsetCardIds(
  candidate: PlaysetCandidate,
  minQty: number,
): string[] {
  const ids: string[] = [];
  let acc = 0;
  for (const c of candidate.cards) {
    if (acc >= minQty) break;
    ids.push(c.id);
    acc += Math.max(1, c.quantity);
  }
  return ids;
}
